import { Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import GavelIcon from '@mui/icons-material/Gavel'
import SecurityIcon from '@mui/icons-material/Security'

import { useProfessionalConsentDialog } from './useProfessionalConsent'
import { DataReuseStep } from './DataReuseStep'
import { SensitiveDataStep } from './SensitiveDataStep'
import { PatientUnderstandingStep } from './PatientUnderstandingStep'
import { Consultation } from '@/core/models/consultation.model'

export type ProfessionalConsentDialogProps = {
  consultationId?: string
  professionalConsent?: Consultation['professionalConsent']
}

export function ProfessionalConsentDialog({ consultationId, professionalConsent }: ProfessionalConsentDialogProps) {
  useLingui()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const {
    isOpen,
    shouldShow,
    currentStep,
    stepChecked,
    setStepChecked,
    isContinueDisabled,
    canGoBack,
    isCurrentStepFinal,
    handleContinue,
    handleBack,
    handleCancel,
  } = useProfessionalConsentDialog({ consultationId, professionalConsent })

  if (!shouldShow) return null

  const isFirstStep = currentStep === 'dataReuse'
  const buttonLabel = isCurrentStepFinal ? <Trans>Accept</Trans> : <Trans>Continue</Trans>
  const buttonEndIcon = isCurrentStepFinal ? <ArrowForwardIcon /> : undefined

  return (
    <Dialog
      disableEscapeKeyDown
      open={isOpen}
      onClose={(_e: {}, reason: string) => {
        if (reason !== 'escapeKeyDown' && reason !== 'backdropClick') handleCancel()
      }}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : '12px', overflow: 'hidden', maxHeight: isMobile ? '100%' : '90vh' } }}
    >
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          px: 2.5,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          {canGoBack && (
            <IconButton onClick={handleBack} size="small" sx={{ color: 'white', p: 0.5 }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
          {isFirstStep ? <SecurityIcon sx={{ color: 'white', fontSize: 22 }} /> : <GavelIcon sx={{ color: 'white', fontSize: 22 }} />}
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'white', letterSpacing: '-0.01em' }}>
            {isFirstStep ? <Trans>Consent management</Trans> : <Trans>Ethical Reminder</Trans>}
          </Typography>
        </Stack>
      </Box>

      <Box sx={{ px: 3, pb: 3, overflowY: 'auto', flex: 1 }}>
        {currentStep === 'dataReuse' && <DataReuseStep checked={stepChecked} onChange={setStepChecked} />}
        {currentStep === 'sensitiveData' && <SensitiveDataStep checked={stepChecked} onChange={setStepChecked} />}
        {currentStep === 'patientUnderstanding' && <PatientUnderstandingStep checked={stepChecked} onChange={setStepChecked} />}
      </Box>

      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Button sx={{ px: 4, py: 1.5, borderRadius: '12px' }} onClick={handleContinue} variant="contained" fullWidth disabled={isContinueDisabled} endIcon={buttonEndIcon}>
          {buttonLabel}
        </Button>
        {isCurrentStepFinal && (
          <Button
            onClick={handleCancel}
            fullWidth
            sx={{ height: isMobile ? 48 : 44, borderRadius: '12px', fontWeight: 600, fontSize: '0.9rem', color: 'text.secondary', '&:hover': { bgcolor: 'grey.200' } }}
          >
            <Trans>Refuse</Trans>
          </Button>
        )}
      </Box>
    </Dialog>
  )
}

export default ProfessionalConsentDialog
